<template>
    <div
        v-if="contentItems.length > 0"
        class="text-carousel"
    >
        <ContentTitle
            v-if="data.shopWithConfidenceTitle"
            :background="background"
            :data="data.shopWithConfidenceTitle"
        />

        <div class="carousel">
            <div
                ref="container"
                class="slides"
            >
                <div
                    v-for="(item, index) in contentItems"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <ContentComponentItem
                        v-for="(content, index2) in item.content"
                        :key="index2"
                        :data="content"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            :aria-label="t('components.text_carousel.button_prev')"
            :disabled="isAtStart"
            class="left"
            icon="angle-left"
            @click="previous"
        />

        <ButtonRound
            :aria-label="t('components.text_carousel.button_next')"
            :disabled="isAtEnd"
            class="right"
            icon="angle-right"
            @click="next"
        />

        <div class="carousel-pagination">
            <div
                v-for="index in pageCount"
                :key="index"
                :class="getClassDot(index)"
                class="carousel-page"
                @click.prevent.stop="goToPage(index)"
            ></div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
    Color,
    ComponentShopWithConfidenceFragment,
    ShopWithConfidenceProductFragment,
} from '@/graphql/api'

import { useCarouselDotsPage } from '@/components/layout/carousels/base/useCarouselDotsPage'
import { getColorForBackground } from '@/components/ui/buttons'
import type { Colors } from '@/components/ui'
import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'
import { cmsColorToButtonColor } from '@/components/cms'

import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import { assertNever } from '@/helpers'
import ContentTitle from '@/components/cms/component/content/ContentTitle.vue'

const { t } = useI18n()
const props = defineProps<{
    data: ComponentShopWithConfidenceFragment
    product: ShopWithConfidenceProductFragment
    background: Color
}>()

const resizeable = computed(() =>
    'isResizeable' in props.product ? props.product.isResizeable : false
)

const returnable = computed(() => props.product.isReturnable)

const greenlandRuby = computed(() =>
    'signatureStone' in props.product
        ? props.product.signatureStone !== null
        : false
)

const ethnicallySourcedDiamonds = computed(
    () => props.product.__typename === 'CatalogueEngagementRing'
)

const contentItems = computed(() => {
    return props.data.components.filter((component) => {
        switch (component.__typename) {
            case 'ShopWithConfidenceResize':
                return resizeable.value
            case 'ShopWithConfidenceGreenlandRuby':
                return greenlandRuby.value
            case 'ShopWithConfidenceEthnicallySourcedDiamonds':
                return ethnicallySourcedDiamonds.value
            case 'ShopWithConfidenceReturnPolicy':
                return returnable.value
            case 'ShopWithConfidenceHandFinished':
            // fallthrough
            case 'ShopWithConfidenceFinancingOptions':
            // fallthrough
            case 'ShopWithConfidenceShipping':
                return true
            default:
                return assertNever(component, true)
        }
    })
})

const thColor = computed((): Colors | undefined => {
    if (!props.background) {
        return undefined
    }

    return getColorForBackground(cmsColorToButtonColor(props.background))
})

const thBackgroundCOlor = computed((): Colors | undefined => {
    if (!props.background) {
        return undefined
    }

    return cmsColorToButtonColor(props.background)
})

const {
    container,
    elements,
    slideWidth,
    previous,
    next,
    current,
    isAtStart,
    isAtEnd,
} = useCarousel(computed(() => contentItems.value.length))

const { pageCount, goToPage, getClassDot } = useCarouselDotsPage(
    container,
    slideWidth,
    contentItems,
    current
)
</script>

<style lang="scss" scoped>
.text-carousel {
    position: relative;
    margin: auto;
    z-index: 10;
    max-width: 1440px;
    color: v-bind(thColor);
    padding: 40px 0;

    @include hover() {
        z-index: 11;
    }

    .carousel {
        width: 1040px;
        max-width: 100%;
        height: auto;
        margin: 0 auto;

        .slides {
            position: relative;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 40px;
            padding: 0 40px;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            &::after {
                content: '';
                padding-right: 40px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                width: 100%;
                flex-shrink: 0;
                display: flex;
                flex-wrap: wrap;
                align-content: flex-start;
                justify-content: center;
                padding: 0 40px;
                text-align: center;

                @include min-width(768) {
                    padding: 0 20px;
                    width: 33%;
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 50px);
        border: 1px solid v-bind(thColor);
        background: v-bind(thColor);
        color: v-bind(thBackgroundCOlor);

        &.disabled {
            opacity: 0;
        }

        &.left {
            left: 15px;

            @include min-width(1600) {
                left: -20px;
            }
        }

        &.right {
            right: 15px;

            @include min-width(1600) {
                right: -20px;
            }
        }
    }

    .carousel-pagination {
        display: flex;
        justify-content: center;
        padding: 40px 0 0;

        .carousel-page {
            flex: 0 0 7px;
            height: 7px;
            border: 1px solid v-bind(thColor);
            margin: 4px;
            border-radius: 50%;
            cursor: pointer;
            transition: background-color 80ms ease-in-out;
            opacity: 0.3;

            &.active {
                background-color: v-bind(thColor);
                opacity: 1;
            }
        }
    }
}
</style>
